import React, { useEffect, useState } from 'react'
import { InputParameterHierarchyType } from '../model'
import {
  InputParametersHierarchyContext,
  useCreateNodeMutation,
  useDeleteNodeMutation,
  useGetTemplateReferencesForAllUsersByContextQuery,
  useUpdateNodeMutation,
} from '../../../store/ai/inputParametersHierarchy'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { Modal } from '../../Modal/Modal'
import { Col, Container, Modal as BootstrapModal, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap'
import { useGetAllUsersQuery } from '../../../store/user/user'
import { User } from '../../../models/User'
import { Button } from '../../Button/Button'
import './AllUserReferencesModal.scss'
import { useListTemplatesQuery } from '../../../store/templateLibrary/templateLibrary'
import { SelectDropdown, SelectOption } from '../../SelectDropdown/SelectDropdown'
import { TemplateScopeMapping } from '../../../views/Setup/Templates/TemplateScopeMapping'
import { calculateHierarchyId } from '../../../views/Setup/Templates/utils'
import { MissingScopeAction } from '../../ScopedBlock/models'
import { ScopedBlock } from '../../ScopedBlock/ScopedBlock'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { getInContextTrackingDetails } from '../../../views/Setup/Templates/tracking'
import { useAccount } from '../../../common/useAccount'
import { LOCK_ALL_OVERRIDE, LOCK_USER_OVERRIDE } from '../../../models/Account'

export interface AllUserReferencesModalProps {
  type: InputParameterHierarchyType
  typeId: string
  context: InputParametersHierarchyContext
  show: boolean
  onHide: () => void
}

type UserReference = {
  nodeId: string
  userId: string
  templateId: string
}

export const AllUserReferencesModal: React.FC<AllUserReferencesModalProps> = ({ type, typeId, context, show, onHide }) => {
  const accountId = useAppSelector(selectAccountId)
  const [userReferences, setUserReferences] = useState<UserReference[]>([])

  const { data: allReferences, isLoading } = useGetTemplateReferencesForAllUsersByContextQuery({
    accountId,
    type,
    typeId,
    context,
  })

  useEffect(() => {
    if (allReferences) {
      setUserReferences(
        allReferences
          .filter(
            (
              r,
            ): r is typeof r & {
              hierarchyId: { userId: string }
            } => 'userId' in r.hierarchyId && !!r.hierarchyId.userId,
          )
          .map((reference) => ({
            nodeId: reference.id,
            userId: reference.hierarchyId.userId,
            templateId: reference.template.id,
          })),
      )
    }
  }, [allReferences])

  const handleOnHide = () => {
    onHide()
  }

  return (
    <Modal
      size='lg'
      show={show}
      onHide={handleOnHide}
      header={<BootstrapModal.Title>Set user-specific templates</BootstrapModal.Title>}
      body={
        <>
          {!isLoading && userReferences ? (
            <AllUserReferencesModalBody type={type} typeId={typeId} context={context} userReferences={userReferences} />
          ) : (
            <div className='d-flex justify-content-center'>
              <Spinner />
            </div>
          )}
        </>
      }
      showFooterActions={false}
      footer={
        <Button variant='primary' onClick={handleOnHide} loading={isLoading}>
          Done
        </Button>
      }
      centered={true}
    />
  )
}

interface AllUserReferencesModalBodyProps {
  type: InputParameterHierarchyType
  typeId: string
  context: InputParametersHierarchyContext
  userReferences: UserReference[]
}

interface UserSelectOption {
  userId: string
  displayName: string
}

const AllUserReferencesModalBody: React.FC<AllUserReferencesModalBodyProps> = ({ type, typeId, context, userReferences }) => {
  const { tracked } = useTracking()
  const { accountId, account } = useAccount()
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const [userSelectOptions, setUserSelectOptions] = useState<UserSelectOption[]>([])
  const [isAddingNewReference, setIsAddingNewReference] = useState(false)
  const { data: users, isFetching: usersFetching } = useGetAllUsersQuery({
    accountId: accountId,
    identityTypes: ['idp'],
  })
  const [triggerCreateNode, { isLoading: isCreating }] = useCreateNodeMutation()
  const isUserOverrideLocked = [LOCK_USER_OVERRIDE, LOCK_ALL_OVERRIDE].includes(account?.enterpriseConfig.lockTemplateOverride ?? '')

  const resolveUserName = (user: User) => {
    const first_last = [user.firstName, user.lastName].filter((i) => i).join(' ')
    const name = user.name
    return name && name !== user.email ? name : first_last || user.email
  }

  useEffect(() => {
    if (users) {
      setUserSelectOptions(
        users.data
          .filter((user) => !!user.email && user.id !== currentUserId)
          .map((user) => ({
            userId: user.id,
            displayName: resolveUserName(user),
          }))
          .sort((a, b) => a.displayName.localeCompare(b.displayName)),
      )
    }
  }, [users, currentUserId])

  const _handleSaveNewReference = async (newReference: NewUserReference) => {
    await triggerCreateNode({
      accountId,
      type,
      typeId,
      node: {
        hierarchyId: calculateHierarchyId(type, { ...context, userId: newReference.userId }),
        templateId: newReference.templateId,
      },
    })
    setIsAddingNewReference(false)
  }

  const handleSaveNewReference = tracked(
    _handleSaveNewReference,
    {
      eventName: TrackEvent.TEMPLATE_ASSIGNED,
    },
    {
      eventDetailsExtractor: (newReference) => getInContextTrackingDetails(newReference.templateId, type, typeId, context),
    },
  )

  return (
    <div>
      <p className='paragraph-medium py-4 my-0'>Assign or remove specific templates to individual users. These override the account default.</p>
      <Container>
        {!usersFetching ? (
          userReferences.map((reference) => (
            <UserReferenceRow
              type={type}
              typeId={typeId}
              context={context}
              key={reference.userId}
              userSelectOptions={userSelectOptions}
              reference={reference}
              readOnly={isUserOverrideLocked}
            />
          ))
        ) : (
          <Spinner />
        )}
        {isAddingNewReference ? (
          <NewUserReferenceRow
            type={type}
            typeId={typeId}
            context={context}
            userSelectOptions={userSelectOptions}
            onSave={handleSaveNewReference}
            onRemove={() => setIsAddingNewReference(false)}
          />
        ) : null}
      </Container>
      {!isUserOverrideLocked ? (
        <Container className='mt-2'>
          <Row>
            <ScopedBlock
              missingScopeAction={MissingScopeAction.UPGRADE}
              quota={{
                quota: 'ai.ipHierarchy.value',
              }}
            >
              <Button
                className='button-medium zocks-text-primary'
                variant='borderless'
                onClick={() => setIsAddingNewReference(true)}
                loading={isCreating}
                disabled={isCreating}
              >
                + Add user
              </Button>
            </ScopedBlock>
          </Row>
        </Container>
      ) : null}
    </div>
  )
}

interface UserReferenceRowProps {
  type: InputParameterHierarchyType
  typeId: string
  context: InputParametersHierarchyContext
  userSelectOptions: UserSelectOption[]
  reference: UserReference
  readOnly?: boolean
}

const UserReferenceRow: React.FC<UserReferenceRowProps> = ({ type, typeId, context, userSelectOptions, reference, readOnly = false }) => {
  const { tracked } = useTracking()
  const accountId = useAppSelector(selectAccountId)
  const [templateOptions, setTemplateOptions] = useState<SelectOption[]>([])
  const [selectedTemplateOption, setSelectedTemplateOption] = useState<SelectOption | undefined>(undefined)
  const { data: templates } = useListTemplatesQuery({
    accountId,
    type,
    typeId,
    defaultTypeId: context.defaultTypeId,
    createdBy: reference.userId,
  })
  const userName = userSelectOptions.find((option) => option.userId === reference.userId)?.displayName || reference.userId

  const [triggerUpdateNode, { isLoading: isUpdating }] = useUpdateNodeMutation()
  const [triggerDeleteNode, { isLoading: isDeleting }] = useDeleteNodeMutation()

  const isLoading = isUpdating || isDeleting

  const _handleSaveReference = async (reference: UserReference) => {
    await triggerUpdateNode({
      accountId,
      type,
      typeId,
      nodeId: reference.nodeId,
      node: { templateId: reference.templateId },
    })
  }

  const handleSaveReference = tracked(
    _handleSaveReference,
    {
      eventName: TrackEvent.TEMPLATE_ASSIGNED,
    },
    {
      condition: !!reference,
      eventDetailsExtractor: () => getInContextTrackingDetails(reference.templateId, type, typeId, context),
    },
  )

  const _handleRemoveReference = async (reference: UserReference) => {
    await triggerDeleteNode({
      accountId,
      type,
      typeId,
      nodeId: reference.nodeId,
    })
  }

  const handleRemoveReference = tracked(_handleRemoveReference, {
    eventName: TrackEvent.TEMPLATE_ASSIGNMENT_REMOVED,
    eventDetails: { ...getInContextTrackingDetails(reference.templateId, type, typeId, context) },
  })

  useEffect(() => {
    if (!templates?.data) {
      return
    }
    const options = templates.data.map((template) => ({
      id: template.id,
      label: template.name,
      displayLabel: (
        <div className='zocks-py-0.125 inline-block w-auto' key={template.id}>
          <span>{template.name}</span>
          <span className='zocks-text-gray-600'> ({TemplateScopeMapping[template.scope].title})</span>
        </div>
      ),
    }))
    setTemplateOptions(options)
    setSelectedTemplateOption(options.find((option) => option.id === reference.templateId))
  }, [templates?.data, reference.templateId])

  return (
    <Row className='user-reference-row align-items-center px-2' key={reference.userId}>
      <Col xs={12} md>
        {userName}
      </Col>
      <Col xs={12} md>
        {templates?.data ? (
          <OverlayTrigger
            show={readOnly ? undefined : false}
            placement='top'
            overlay={<Tooltip>Template override is disabled at the enterprise level</Tooltip>}
          >
            {({ ref, ...triggerHandler }) => (
              <ScopedBlock
                missingScopeAction={MissingScopeAction.DISABLE}
                quota={{
                  quota: 'ai.ipHierarchy.value',
                }}
              >
                <div ref={ref} {...triggerHandler}>
                  <SelectDropdown
                    placeholder='Select template...'
                    loading={isLoading}
                    initialSelectedOption={selectedTemplateOption}
                    options={templateOptions}
                    onOptionSelected={(option) => handleSaveReference({ ...reference, templateId: option.id })}
                    disabled={readOnly}
                  />
                </div>
              </ScopedBlock>
            )}
          </OverlayTrigger>
        ) : (
          <Spinner />
        )}
      </Col>
      <Col xs='auto'>
        {!readOnly ? (
          <OverlayTrigger placement='top' overlay={<Tooltip>Remove user-specific template</Tooltip>}>
            {({ ref, ...triggerHandler }) => (
              <ScopedBlock
                missingScopeAction={MissingScopeAction.DISABLE}
                quota={{
                  quota: 'ai.ipHierarchy.value',
                }}
              >
                <Button ref={ref} variant='borderless' loading={isLoading} onClick={() => handleRemoveReference(reference)} {...triggerHandler}>
                  <i className='bi bi-trash' />
                </Button>
              </ScopedBlock>
            )}
          </OverlayTrigger>
        ) : null}
      </Col>
    </Row>
  )
}

type NewUserReference = {
  userId: string
  templateId: string
}

interface NewUserReferenceRowProps {
  type: InputParameterHierarchyType
  typeId: string
  context: InputParametersHierarchyContext
  userSelectOptions: UserSelectOption[]
  onSave: (reference: NewUserReference) => void
  onRemove: () => void
}

const NewUserReferenceRow: React.FC<NewUserReferenceRowProps> = ({ type, typeId, context, userSelectOptions, onSave, onRemove }) => {
  const accountId = useAppSelector(selectAccountId)
  const [templateOptions, setTemplateOptions] = useState<SelectOption[]>([])
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined)
  const { data: templates, isFetching: isTemplatesLoading } = useListTemplatesQuery(
    {
      accountId,
      type,
      typeId,
      defaultTypeId: context.defaultTypeId,
      createdBy: selectedUserId,
    },
    { skip: !selectedUserId },
  )

  useEffect(() => {
    if (!templates?.data) {
      return
    }
    const options = templates.data.map((template) => ({
      id: template.id,
      label: template.name,
      displayLabel: (
        <div className='zocks-py-0.125 inline-block w-auto' key={template.id}>
          <span>{template.name}</span>
          <span className='zocks-text-gray-600'> ({TemplateScopeMapping[template.scope].title})</span>
        </div>
      ),
    }))
    setTemplateOptions(options)
  }, [templates?.data, selectedUserId])

  return (
    <Row className='user-reference-row align-items-center px-2'>
      <Col xs={12} md>
        <ScopedBlock
          missingScopeAction={MissingScopeAction.DISABLE}
          quota={{
            quota: 'ai.ipHierarchy.value',
          }}
        >
          <SelectDropdown
            placeholder='Select user...'
            initialSelectedOption={null}
            options={userSelectOptions.map((option) => ({ id: option.userId, label: option.displayName }))}
            onOptionSelected={(option) => setSelectedUserId(option.id)}
            onDeselect={() => setSelectedUserId(undefined)}
          />
        </ScopedBlock>
      </Col>
      <Col xs={12} md>
        {selectedUserId ? (
          templates?.data ? (
            <ScopedBlock
              missingScopeAction={MissingScopeAction.DISABLE}
              quota={{
                quota: 'ai.ipHierarchy.value',
              }}
            >
              <SelectDropdown
                placeholder='Select template...'
                initialSelectedOption={null}
                loading={isTemplatesLoading}
                options={templateOptions}
                disabled={!selectedUserId}
                onOptionSelected={(option) => onSave({ userId: selectedUserId!, templateId: option.id })}
              />
            </ScopedBlock>
          ) : (
            <Spinner />
          )
        ) : null}
      </Col>
      <Col xs='auto'>
        <OverlayTrigger placement='top' overlay={<Tooltip>Remove user-specific template</Tooltip>}>
          {({ ref, ...triggerHandler }) => (
            <ScopedBlock
              missingScopeAction={MissingScopeAction.DISABLE}
              quota={{
                quota: 'ai.ipHierarchy.value',
              }}
            >
              <Button ref={ref} variant='borderless' onClick={onRemove} {...triggerHandler}>
                <i className='bi bi-trash' />
              </Button>
            </ScopedBlock>
          )}
        </OverlayTrigger>
      </Col>
    </Row>
  )
}
